import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {NonEmptyString, StringUnion, EmptyString} from 'Core/models/EmptyString';
import {isValidZipCode, ValidZipCode, ZipCode, EmptyZipCode} from 'ApplicationEntry/models/ZipCode';

export class InvalidSecondaryAddressee {
  public readonly key = 'InvalidSecondaryAddressee';

  constructor(
    public readonly name: StringUnion,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode
  ) {}
}

export class ValidSecondaryAddressee {
  public static create(
    name: StringUnion,
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode
  ) {
    return isValidZipCode(zip)
      ? new ValidSecondaryAddressee(name, address, addressLine2, city, state, zip)
      : new InvalidSecondaryAddressee(name, address, addressLine2, city, state, zip);
  }

  public readonly key = 'ValidSecondaryAddressee';
  constructor(
    public readonly name: StringUnion,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode
  ) {}
}

export type SecondaryAddressee = InvalidSecondaryAddressee | ValidSecondaryAddressee;

export type SecondaryAddresseeOptions<TReturn> = ToPartialMatchOptions<SecondaryAddressee, 'key', TReturn>;

export const matchSecondaryAddressee = <TReturn>(options: SecondaryAddresseeOptions<TReturn>) => (
  app: SecondaryAddressee
): TReturn => {
  switch (app.key) {
    case 'InvalidSecondaryAddressee':
      return options.InvalidSecondaryAddressee ? options.InvalidSecondaryAddressee(app) : options.default(app);
    case 'ValidSecondaryAddressee':
      return options.ValidSecondaryAddressee ? options.ValidSecondaryAddressee(app) : options.default(app);
  }
};

export const changeName = (name: string) => ({address, addressLine2, city, state, zip}: SecondaryAddressee) =>
  ValidSecondaryAddressee.create(NonEmptyString.create(name), address, addressLine2, city, state, zip);
export const changeAddress = (address: string) => ({name, addressLine2, city, state, zip}: SecondaryAddressee) =>
  ValidSecondaryAddressee.create(name, NonEmptyString.create(address), addressLine2, city, state, zip);

export const changeAddressLine2 = (addressLine2: string) => ({name, address, city, state, zip}: SecondaryAddressee) =>
  ValidSecondaryAddressee.create(name, address, NonEmptyString.create(addressLine2), city, state, zip);
export const changeCity = (city: string) => ({name, address, addressLine2, state, zip}: SecondaryAddressee) =>
  ValidSecondaryAddressee.create(name, address, addressLine2, NonEmptyString.create(city), state, zip);
export const changeState = (state: string) => ({name, address, addressLine2, city, zip}: SecondaryAddressee) =>
  ValidSecondaryAddressee.create(name, address, addressLine2, city, NonEmptyString.create(state), zip);
export const changeZip = (zip: ZipCode) => ({name, address, addressLine2, city, state}: SecondaryAddressee) =>
  ValidSecondaryAddressee.create(name, address, addressLine2, city, state, zip);

export const EmptySecondaryAddressee = new ValidSecondaryAddressee(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode
);

export const isValidSecondaryAddressee = (
  secondaryAddressee: SecondaryAddressee
): secondaryAddressee is ValidSecondaryAddressee => secondaryAddressee.key === 'ValidSecondaryAddressee';
